import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Save, Loader2, Receipt, IndianRupee, Hash, FileText, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BillingPreferencesTab — clinic-scoped defaults for every new invoice.
 *
 * Persists to `clinics.billing_prefs` via `PUT /api/settings/billing-preferences`.
 * Values are snapshot-copied onto each invoice at create time, so
 * later edits DO NOT retroactively rewrite historical invoices.
 * Only clinic_owner / super_admin can save.
 */
export default function BillingPreferencesTab() {
  const [prefs, setPrefs] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/billing-preferences`);
      setPrefs(r.data);
      setInitial(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load preferences');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (patch) => setPrefs((p) => ({ ...(p || {}), ...patch }));

  const isDirty = prefs && initial && JSON.stringify(prefs) !== JSON.stringify(initial);

  const save = async () => {
    if (!prefs || saving) return;
    setSaving(true); setErr(''); setMsg('');
    try {
      const r = await axios.put(`${API}/settings/billing-preferences`, prefs);
      setPrefs(r.data);
      setInitial(r.data);
      setMsg('Saved. New invoices will use these defaults.');
      setTimeout(() => setMsg(''), 3500);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="p-6 text-sm text-slate-400 italic">Loading billing preferences…</div>;
  }

  const termsPreset = [
    { value: 0,  label: 'Due on receipt' },
    { value: 15, label: 'Net 15' },
    { value: 30, label: 'Net 30' },
    { value: 45, label: 'Net 45' },
    { value: 60, label: 'Net 60' },
  ];
  const isCustomTerm = !termsPreset.some((t) => t.value === prefs.payment_terms_days);

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4" data-testid="billing-prefs-tab">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
          <Receipt size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900" data-testid="billing-prefs-heading">
            Billing Preferences
          </h2>
          <p className="text-[12px] text-slate-500">
            Defaults applied to every <strong>new</strong> invoice. Existing invoices are not touched.
          </p>
        </div>
      </header>

      {err && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="billing-prefs-err">{err}</div>}
      {msg && <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="billing-prefs-msg">{msg}</div>}

      {/* Payment terms */}
      <Section icon={<IndianRupee size={14} />} title="Payment terms" subtitle="Sets the invoice due date automatically.">
        <div className="flex flex-wrap gap-1.5" data-testid="billing-prefs-terms-picker">
          {termsPreset.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => update({ payment_terms_days: t.value })}
              data-testid={`billing-prefs-terms-${t.value}`}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded border transition ${
                prefs.payment_terms_days === t.value && !isCustomTerm
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => update({ payment_terms_days: 21 })}
            data-testid="billing-prefs-terms-custom-btn"
            className={`px-2.5 py-1 text-[11px] font-semibold rounded border transition ${
              isCustomTerm
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
            }`}
          >Custom</button>
          {isCustomTerm && (
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0} max={365}
                value={prefs.payment_terms_days}
                onChange={(e) => update({ payment_terms_days: Math.max(0, Math.min(365, parseInt(e.target.value || '0', 10))) })}
                data-testid="billing-prefs-terms-custom"
                className="w-16 px-2 py-1 text-[11px] border border-slate-300 rounded"
              />
              <span className="text-[11px] text-slate-500">days</span>
            </div>
          )}
        </div>
      </Section>

      {/* GST defaults */}
      <Section icon={<IndianRupee size={14} />} title="GST defaults" subtitle="Pre-fills every new invoice line.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Default GST rate (%)</div>
            <input
              type="number"
              min={0} max={100} step={0.1}
              value={prefs.default_gst_rate}
              onChange={(e) => update({ default_gst_rate: parseFloat(e.target.value || '0') })}
              data-testid="billing-prefs-gst-rate"
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
            />
          </label>
          <label className="block">
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Price entry mode</div>
            <div className="flex gap-1.5">
              <PickPill
                active={prefs.default_gst_inclusive}
                onClick={() => update({ default_gst_inclusive: true })}
                testid="billing-prefs-gst-inclusive"
              >Inclusive of GST</PickPill>
              <PickPill
                active={!prefs.default_gst_inclusive}
                onClick={() => update({ default_gst_inclusive: false })}
                testid="billing-prefs-gst-exclusive"
              >Exclusive (GST added)</PickPill>
            </div>
          </label>
        </div>
      </Section>

      {/* Invoice numbering */}
      <Section icon={<Hash size={14} />} title="Invoice numbering" subtitle="Format applied to newly-generated invoice numbers only. Historical invoices are unchanged.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Prefix</div>
            <input
              type="text"
              maxLength={16}
              value={prefs.invoice_prefix}
              onChange={(e) => update({ invoice_prefix: e.target.value.replace(/[^A-Za-z0-9\-]/g, '').toUpperCase() })}
              data-testid="billing-prefs-invoice-prefix"
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono"
              placeholder="INV"
            />
            <div className="text-[10px] text-slate-500 mt-1">A-Z, 0-9, dashes only.</div>
          </label>
          <label className="block">
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Reset behaviour</div>
            <div className="flex gap-1.5">
              <PickPill
                active={prefs.invoice_number_reset === 'yearly'}
                onClick={() => update({ invoice_number_reset: 'yearly' })}
                testid="billing-prefs-reset-yearly"
              >Reset yearly</PickPill>
              <PickPill
                active={prefs.invoice_number_reset === 'never'}
                onClick={() => update({ invoice_number_reset: 'never' })}
                testid="billing-prefs-reset-never"
              >Never reset</PickPill>
            </div>
          </label>
        </div>
        <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
          Next invoice preview:{' '}
          <span className="font-mono font-bold text-slate-800" data-testid="billing-prefs-preview">
            {prefs.invoice_number_reset === 'yearly'
              ? `${prefs.invoice_prefix || 'INV'}/${new Date().getFullYear()}/000042`
              : `${prefs.invoice_prefix || 'INV'}-000042`}
          </span>
        </div>
      </Section>

      {/* Printed strips */}
      <Section icon={<FileText size={14} />} title="Printed on every invoice">
        <div className="space-y-3">
          <TextArea
            label="Invoice footer note"
            hint="1-2 lines below the totals block. Common uses: thank-you note, warranty policy summary, cheque-payable info."
            value={prefs.invoice_footer_note || ''}
            onChange={(v) => update({ invoice_footer_note: v || null })}
            testid="billing-prefs-footer"
            maxLength={1000}
            rows={2}
          />
          <TextArea
            label="Bank / UPI payee details"
            hint="Printed as a “How to pay” strip so patients can settle balances by transfer."
            value={prefs.bank_payee_details || ''}
            onChange={(v) => update({ bank_payee_details: v || null })}
            testid="billing-prefs-bank"
            maxLength={1000}
            rows={3}
          />
          <TextArea
            label="Terms & conditions"
            hint="Long-form legal / warranty block printed at the bottom of every invoice."
            value={prefs.terms_and_conditions || ''}
            onChange={(v) => update({ terms_and_conditions: v || null })}
            testid="billing-prefs-tnc"
            maxLength={4000}
            rows={4}
          />
        </div>
      </Section>

      {/* Quotation defaults — separate from invoice T&C so clinics can word
          the proposal-stage terms differently. Rendered on the Quotation PDF
          endpoint (see /api/ha/quotations/{no}/quotation.pdf). */}
      <Section
        icon={<FileText size={14} />}
        title="Printed on every quotation"
        subtitle="Applied to the printable Quotation PDF only. Invoice-level T&C above are unaffected."
      >
        <div className="space-y-3">
          <TextArea
            label="Quotation terms & conditions"
            hint="Proposal-stage terms — e.g. ‘Prices valid 15 days’, ‘Subject to availability’, trial-return policy. If left blank a friendly default is used."
            value={prefs.quotation_terms_and_conditions || ''}
            onChange={(v) => update({ quotation_terms_and_conditions: v || null })}
            testid="billing-prefs-quotation-tnc"
            maxLength={4000}
            rows={5}
          />
          <TextArea
            label="Quotation footer note"
            hint="Small print at the very bottom of the Quotation PDF."
            value={prefs.quotation_footer_note || ''}
            onChange={(v) => update({ quotation_footer_note: v || null })}
            testid="billing-prefs-quotation-footer"
            maxLength={1000}
            rows={2}
          />
        </div>
      </Section>

      {/* Automated payouts */}
      <Section
        icon={<Mail size={14} />}
        title="Automated payouts"
        subtitle="Auto-email doctor commission summaries and auto-create weekly referral-partner payouts."
      >
        <label
          htmlFor="monthly-commission-email-toggle"
          className="flex items-start gap-3 cursor-pointer select-none"
          data-testid="billing-prefs-monthly-commission-email-label"
        >
          <span className="relative inline-flex items-center mt-0.5">
            <input
              id="monthly-commission-email-toggle"
              type="checkbox"
              checked={!!prefs.monthly_commission_email}
              onChange={(e) => update({ monthly_commission_email: e.target.checked })}
              data-testid="billing-prefs-monthly-commission-email"
              data-state={prefs.monthly_commission_email ? 'checked' : 'unchecked'}
              aria-checked={!!prefs.monthly_commission_email}
              className="peer sr-only"
            />
            <span className="w-9 h-5 bg-slate-300 peer-checked:bg-emerald-600 rounded-full transition-colors" />
            <span className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
          </span>
          <span className="flex-1">
            <span className="block text-[12.5px] font-semibold text-slate-800">Monthly Commission Email</span>
            <span className="block text-[11px] text-slate-500 mt-0.5">
              Sends one <b>per-branch summary</b> to each Branch Admin (Clinic Owner scoped to that branch) and a
              combined summary to the Clinic Owner. Uses paid invoices for the calendar month and each doctor's
              existing commission rules. Toggle OFF to stop.
            </span>
          </span>
        </label>

        <label
          htmlFor="weekly-partner-payout-toggle"
          className="flex items-start gap-3 cursor-pointer select-none mt-4 pt-4 border-t border-slate-100"
          data-testid="billing-prefs-weekly-partner-payout-label"
        >
          <span className="relative inline-flex items-center mt-0.5">
            <input
              id="weekly-partner-payout-toggle"
              type="checkbox"
              checked={!!prefs.weekly_partner_payout_email}
              onChange={(e) => update({ weekly_partner_payout_email: e.target.checked })}
              data-testid="billing-prefs-weekly-partner-payout"
              data-state={prefs.weekly_partner_payout_email ? 'checked' : 'unchecked'}
              aria-checked={!!prefs.weekly_partner_payout_email}
              className="peer sr-only"
            />
            <span className="w-9 h-5 bg-slate-300 peer-checked:bg-emerald-600 rounded-full transition-colors" />
            <span className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
          </span>
          <span className="flex-1">
            <span className="block text-[12.5px] font-semibold text-slate-800">
              Weekly Partner Payout Automation
              <span className="ml-2 inline-block text-[9.5px] font-bold uppercase tracking-widest bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Premium</span>
            </span>
            <span className="block text-[11px] text-slate-500 mt-0.5">
              Every Monday 07:00 IST, creates a pending payout for each active referral partner
              covering last week (Mon→Sun), applies any recovery-ledger deductions, notifies the
              partner by email, and sends a summary to the Clinic Owner. Overlapping windows are
              skipped so re-runs are safe. Toggle OFF to stop.
            </span>
          </span>
        </label>
      </Section>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 px-3 py-2 flex items-center justify-between rounded-b">
        <span className="text-[11px] text-slate-500">
          {isDirty ? 'Unsaved changes.' : 'All changes saved.'}
        </span>
        <button
          type="button"
          disabled={!isDirty || saving}
          onClick={save}
          data-testid="billing-prefs-save"
          className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded shadow-sm disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}

const Section = ({ icon, title, subtitle, children }) => (
  <section className="bg-white border border-slate-200 rounded-lg p-4">
    <header className="flex items-center gap-1.5 mb-2">
      <span className="text-slate-500">{icon}</span>
      <h3 className="text-[13px] font-bold text-slate-900">{title}</h3>
    </header>
    {subtitle && <p className="text-[11px] text-slate-500 mb-3 -mt-1">{subtitle}</p>}
    {children}
  </section>
);

const PickPill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-2.5 py-1 text-[11px] font-semibold rounded border transition ${
      active
        ? 'bg-emerald-600 text-white border-emerald-600'
        : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
    }`}
  >{children}</button>
);

const TextArea = ({ label, hint, value, onChange, testid, maxLength, rows = 3 }) => (
  <label className="block">
    <div className="text-[11px] font-semibold text-slate-600 mb-0.5">{label}</div>
    {hint && <div className="text-[10px] text-slate-500 mb-1">{hint}</div>}
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
      rows={rows}
      data-testid={testid}
      className="w-full px-2 py-1.5 text-[12px] border border-slate-300 rounded font-mono resize-y"
    />
    <div className="text-[9px] text-slate-400 mt-0.5">{(value || '').length} / {maxLength}</div>
  </label>
);
